class SEmergencyVetTriageTool extends HTMLElement {
  constructor() {
    super();

    this.initialized = false;
    this.abortController = null;

    this.redFlags = [
      "Collapsed, unconscious, or can't stand up",
      "Struggling to breathe, choking, or pale / blue / white gums",
      "Bloated or swollen belly, or trying to vomit with nothing coming up (dogs)",
      "A seizure lasting more than 5 minutes, or repeated seizures",
      "Bleeding heavily that won't stop",
      "A male cat straining in the litter tray with little or no urine",
      "Suspected poison, snake bite, or sudden facial swelling",
      "Hit by a car, a bad fall, or another major trauma",
      "In labour: over 30 min of hard straining, or 2+ hours between pups/kittens",
      "Obvious severe pain, or a hard, painful belly",
      "Overheating on a hot day: heavy panting, drooling, wobbly or collapsed"
    ];

    this.concerns = {
      vomit: {
        questions: [
          {
            id: "v-freq",
            label: "How often in the last few hours?",
            options: [
              { text: "Once, and otherwise bright", level: 1 },
              { text: "A few times but still perky", level: 2 },
              { text: "Repeatedly / can't keep water down", level: 3 }
            ]
          },
          {
            id: "v-blood",
            label: "Any blood in the vomit or diarrhoea?",
            options: [
              { text: "No", level: 1 },
              { text: "A little / streaks", level: 2 },
              { text: "A lot, or it looks like coffee grounds or is very dark", level: 3 }
            ]
          },
          {
            id: "v-both",
            label: "Both vomiting and diarrhoea together?",
            options: [
              { text: "No, just one", level: 1 },
              { text: "Yes, both", level: 2, flag: "gastro" }
            ]
          }
        ]
      },
      noteat: {
        questions: [
          {
            id: "n-dur",
            label: "How long off their food?",
            options: [
              { text: "Less than a day, otherwise normal", level: 1 },
              { text: "About 1–2 days", level: 2 },
              { text: "More than 2 days", level: 3 }
            ]
          },
          {
            id: "n-flat",
            label: "How are they in themselves?",
            options: [
              { text: "Bright, just fussy", level: 1 },
              { text: "Quiet and a bit flat", level: 2 },
              { text: "Very flat, weak, or hiding", level: 3 }
            ]
          }
        ]
      },
      breathing: {
        questions: [
          {
            id: "b-effort",
            label: "What does the breathing look like?",
            options: [
              { text: "Occasional cough, breathing normally", level: 1 },
              { text: "Faster than normal, or a persistent cough", level: 2 },
              { text: "Laboured, open-mouth (cat), or belly heaving", level: 3 }
            ]
          },
          {
            id: "b-colour",
            label: "Gum colour (lift the lip and look)",
            options: [
              { text: "Healthy pink", level: 1 },
              { text: "Not sure", level: 2 },
              { text: "Pale, blue, grey or brick red", level: 3 }
            ]
          }
        ]
      },
      urinate: {
        questions: [
          {
            id: "u-what",
            label: "What's happening?",
            options: [
              { text: "Weeing more, or the odd accident", level: 1 },
              { text: "Going often, small amounts, maybe blood", level: 2 },
              { text: "Straining with little or nothing coming out", level: 3, flag: "block" },
              { text: "Straining to poo, or you can't tell wee from poo", level: 1, flag: "constip" }
            ]
          },
          {
            id: "u-pain",
            label: "Signs of pain or distress while trying?",
            options: [
              { text: "No", level: 1 },
              { text: "Some crying / restlessness", level: 2 },
              { text: "Crying out, licking, very distressed", level: 3 }
            ]
          }
        ]
      },
      limp: {
        questions: [
          {
            id: "l-weight",
            label: "Are they using the leg?",
            options: [
              { text: "Yes, just a mild limp", level: 1 },
              { text: "Touching it down but favouring it", level: 2 },
              { text: "Won't put any weight on it, or it looks deformed", level: 3 }
            ]
          },
          {
            id: "l-cause",
            label: "Any known trauma?",
            options: [
              { text: "No obvious cause", level: 1 },
              { text: "A knock, slip or twist", level: 2 },
              { text: "A fall, car or other big impact", level: 3 }
            ]
          }
        ]
      },
      eye: {
        questions: [
          {
            id: "e-what",
            label: "What's wrong with the eye?",
            options: [
              { text: "A bit weepy or slightly red", level: 1 },
              { text: "Red, squinting, or held shut / clearly sore", level: 2 },
              { text: "Bulging, an injury, cloudy suddenly, or chemical splash", level: 3 }
            ]
          }
        ]
      },
      skin: {
        questions: [
          {
            id: "s-what",
            label: "What are you seeing?",
            options: [
              { text: "An itch, small lump or mild ear gunk", level: 1 },
              { text: "A sore hot spot, painful ear, or a wound that needs a look", level: 2 },
              { text: "Fast-spreading facial swelling, or a deep / bleeding wound", level: 3 }
            ]
          }
        ]
      },
      ate: {
        questions: [
          {
            id: "a-what",
            label: "What did they eat?",
            options: [
              { text: "A bit of unusual food, likely harmless", level: 1 },
              { text: "An object that could get stuck (toy, sock, bone, corn cob, string)", level: 2, flag: "object" },
              { text: "A known toxin (chocolate, xylitol/birch sugar, grapes/raisins, onion/garlic, rat bait, lilies, medications, etc.)", level: 3, flag: "tox" }
            ]
          }
        ]
      },
      behaviour: {
        questions: [
          {
            id: "be-what",
            label: "How are they behaving?",
            options: [
              { text: "A little off, but eating and moving normally", level: 1 },
              { text: "Restless, clingy, or not themselves", level: 2 },
              { text: "Disoriented, circling, star-gazing, or unresponsive", level: 3 }
            ]
          }
        ]
      },
      other: {
        questions: [
          {
            id: "o-sev",
            label: "How worried are you, honestly?",
            options: [
              { text: "Mild niggle, they seem well", level: 1 },
              { text: "Something's not right and it's getting worse", level: 2 },
              { text: "I'm genuinely scared for them right now", level: 3 }
            ]
          }
        ]
      }
    };
  }

  connectedCallback() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.abortController = new AbortController();
    this.cacheElements();
    this.renderRedFlags();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    this.initialized = false;
  }

  cacheElements() {
    this.elements = {
      form: this.querySelector("[data-evtt-form]"),
      concern: this.querySelector("[data-evtt-concern]"),
      followups: this.querySelector("[data-evtt-followups]"),
      redFlags: this.querySelector("[data-evtt-red-flags]"),
      assess: this.querySelector("[data-evtt-assess]"),
      error: this.querySelector("[data-evtt-error]"),
      result: this.querySelector("[data-evtt-result]"),
      resultHead: this.querySelector("[data-evtt-result-head]"),
      resultTag: this.querySelector("[data-evtt-result-tag]"),
      resultTitle: this.querySelector("[data-evtt-result-title]"),
      resultSubtitle: this.querySelector("[data-evtt-result-subtitle]"),
      doNowHeading: this.querySelector("[data-evtt-do-now-heading]"),
      doNow: this.querySelector("[data-evtt-do-now]"),
      watch: this.querySelector("[data-evtt-watch]"),
      ctaHeading: this.querySelector("[data-evtt-cta-heading]"),
      ctaText: this.querySelector("[data-evtt-cta-text]"),
      ctaActions: this.querySelector("[data-evtt-cta-actions]"),
      restart: this.querySelector("[data-evtt-restart]")
    };
  }

  bindEvents() {
    const { signal } = this.abortController;

    this.elements.concern?.addEventListener("change", () => this.renderFollowups(), { signal });
    this.elements.assess?.addEventListener("click", () => this.assess(), { signal });
    this.elements.restart?.addEventListener("click", () => this.restart(), { signal });
    this.elements.redFlags?.addEventListener("change", (event) => this.handleRedFlagChange(event), { signal });
  }

  renderRedFlags() {
    if (!this.elements.redFlags) {
      return;
    }

    this.elements.redFlags.innerHTML = "";

    this.redFlags.forEach((text, index) => {
      const label = document.createElement("label");
      const input = document.createElement("input");
      const span = document.createElement("span");

      label.className = "s-emergency-vet-triage-tool__red-flag-item";
      input.type = "checkbox";
      input.dataset.evttRedFlag = String(index);
      input.setAttribute("aria-label", text);
      span.textContent = text;

      label.append(input, span);
      this.elements.redFlags.appendChild(label);
    });
  }

  handleRedFlagChange(event) {
    const input = event.target.closest("[data-evtt-red-flag]");

    if (!input) {
      return;
    }

    input.closest(".s-emergency-vet-triage-tool__red-flag-item")?.classList.toggle("is-selected", input.checked);
  }

  renderFollowups() {
    const concern = this.elements.concern?.value;

    this.elements.followups.innerHTML = "";

    if (!concern || !this.concerns[concern]) {
      return;
    }

    this.concerns[concern].questions.forEach((question) => {
      const block = document.createElement("div");
      const label = document.createElement("label");
      const select = document.createElement("select");
      const placeholder = document.createElement("option");
      const uniqueId = `${this.dataset.sectionId}-${question.id}`;

      block.className = "s-emergency-vet-triage-tool__field-block";
      label.className = "s-emergency-vet-triage-tool__field-label s-emergency-vet-triage-tool__followup-label";
      label.htmlFor = uniqueId;
      label.textContent = question.label;

      select.id = uniqueId;
      select.dataset.evttFollowup = question.id;

      placeholder.value = "";
      placeholder.textContent = "Choose…";
      select.appendChild(placeholder);

      question.options.forEach((option, index) => {
        const optionElement = document.createElement("option");

        optionElement.value = String(index);
        optionElement.textContent = option.text;
        optionElement.dataset.level = String(option.level);
        optionElement.dataset.flag = option.flag || "";
        select.appendChild(optionElement);
      });

      block.append(label, select);
      this.elements.followups.appendChild(block);
    });
  }

  assess() {
    this.clearError();

    const species = this.querySelector("[data-evtt-species]:checked")?.value || "dog";
    const risks = this.getRisks();
    const fragile = risks.young || risks.senior || risks.pregnant || risks.chronic;
    const redFlagHit = [...this.querySelectorAll("[data-evtt-red-flag]")].some((input) => input.checked);

    if (redFlagHit) {
      this.renderResult(3, { reason: "redflag", species, flags: [], fragile });
      return;
    }

    const concern = this.elements.concern?.value;

    if (!concern) {
      this.showError("Tick any emergency signs above, or pick the main concern so we can help.");
      return;
    }

    const answers = [...this.querySelectorAll("[data-evtt-followup]")];
    const unanswered = answers.some((select) => select.value === "");

    if (answers.length === 0 || unanswered) {
      this.showError("Answer every follow-up question so the result is meaningful.");
      return;
    }

    let level = 1;
    const flags = [];

    answers.forEach((select) => {
      const selectedOption = select.options[select.selectedIndex];
      const selectedLevel = Number.parseInt(selectedOption.dataset.level || "1", 10);
      const flag = selectedOption.dataset.flag;

      level = Math.max(level, selectedLevel);

      if (flag) {
        flags.push(flag);
      }
    });

    if (flags.includes("block")) {
      level = 3;
    }

    if (flags.includes("gastro") && risks.young) {
      level = Math.max(level, 2);
    }

    if (concern === "urinate" && species === "cat" && flags.includes("constip") && level < 2) {
      level = 2;
      flags.push("catstrain");
    }

    if (risks.brachy && concern === "breathing" && level < 2) {
      level = 2;
      flags.push("brachy");
    }

    if (flags.includes("tox")) {
      level = 3;
    }

    const systemicConcerns = ["vomit", "noteat", "breathing", "urinate", "ate", "behaviour"];

    if (fragile && systemicConcerns.includes(concern) && level === 1) {
      level = 2;
    }

    this.renderResult(level, { reason: concern, species, flags, fragile });
  }

  getRisks() {
    const values = {};

    this.querySelectorAll("[data-evtt-risk]").forEach((input) => {
      values[input.dataset.evttRisk] = input.checked;
    });

    return values;
  }

  renderResult(level, context) {
    const chatUrl = this.dataset.chatUrl || "/";
    const emergencyUrl = this.dataset.emergencyUrl || chatUrl;

    if (level === 3) {
      this.renderEmergencyResult(context, emergencyUrl);
    } else if (level === 2) {
      this.renderUrgentResult(context, chatUrl);
    } else {
      this.renderMonitorResult(chatUrl);
    }

    this.elements.result.hidden = false;
    this.elements.result.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  renderEmergencyResult(context, emergencyUrl) {
    this.elements.resultHead.className = "s-emergency-vet-triage-tool__result-head is-high";
    this.elements.resultTag.textContent = "Emergency — go now";

    if (context.reason === "redflag") {
      this.elements.resultTitle.textContent = "This needs an emergency vet right now.";
      this.elements.resultSubtitle.textContent = "You selected a sign that can become life-threatening quickly. Do not wait to see if it settles.";
    } else if (context.flags.includes("tox")) {
      this.elements.resultTitle.textContent = "Treat this as a poisoning emergency.";
      this.elements.resultSubtitle.textContent = "With a known toxin, minutes matter and your pet may look normal before serious signs appear.";
    } else if (context.flags.includes("block")) {
      this.elements.resultTitle.textContent = "Possible blocked bladder — this is an emergency.";
      this.elements.resultSubtitle.textContent = `Straining with little or no urine${context.species === "cat" ? ", especially in a male cat," : ""} can become life-threatening within hours.`;
    } else {
      this.elements.resultTitle.textContent = "On your answers, this needs a vet now.";
      this.elements.resultSubtitle.textContent = "The signs described sit in emergency territory. It is safer to be assessed immediately.";
    }

    this.elements.doNowHeading.textContent = "Do this now";

    const items = [
      "<strong>Call your nearest open or emergency vet clinic and head in.</strong> Ring ahead so the team is ready for you."
    ];

    if (context.flags.includes("tox")) {
      const poisonAu = this.dataset.poisonAu || "1300869738";
      const poisonNz = this.dataset.poisonNz || "0800869738";

      items.unshift(`<strong>Call the Animal Poisons Helpline:</strong> <a href="tel:${this.escapeAttribute(poisonAu)}">${this.formatPhone(poisonAu)}</a> (AU) or <a href="tel:${this.escapeAttribute(poisonNz)}">${this.formatPhone(poisonNz)}</a> (NZ).`);
      items.push("Take the packaging, plant or a photo of what they ate, and note how much and when.");
      items.push("<strong>Do not make them vomit</strong> unless a veterinarian or poison adviser specifically tells you to.");
    } else {
      items.push("Keep your pet calm, warm and as still as possible while travelling.");
      items.push("Take a photo or note of anything unusual, including vomit, urine or the injury.");
    }

    items.push("Do not give food, water or human medicine unless a veterinarian instructs you to.");

    this.renderList(this.elements.doNow, items);
    this.renderList(this.elements.watch, [
      "If they collapse or their gums turn pale or blue, call the clinic while travelling.",
      "Bring another person when possible so one person can focus on your pet."
    ]);

    this.elements.ctaHeading.textContent = "On your way? A vet can help you prepare.";
    this.elements.ctaText.textContent = "Contact a veterinarian while you travel so you know what to do and what information to have ready.";
    this.renderCta(emergencyUrl, "Find emergency vet support");
  }

  renderUrgentResult(context, chatUrl) {
    this.elements.resultHead.className = "s-emergency-vet-triage-tool__result-head is-medium";
    this.elements.resultTag.textContent = "Urgent — see a vet today";
    this.elements.resultTitle.textContent = "Get your pet seen by a vet today.";

    let subtitle = "This should not be left overnight. Phone your clinic and request a same-day assessment.";

    if (context.fragile) {
      subtitle += " Their age or health status lowers the threshold for being examined.";
    }

    if (context.flags.includes("brachy")) {
      subtitle += " Flat-faced breeds can deteriorate quickly when breathing changes are present.";
    }

    this.elements.resultSubtitle.textContent = subtitle;
    this.elements.doNowHeading.textContent = "Do this today";

    const items = [
      "<strong>Phone your vet now and ask for a same-day appointment.</strong>",
      "If your regular clinic is closed, call an emergency or after-hours clinic for timing advice.",
      "Note when the signs started and how they have changed."
    ];

    if (context.flags.includes("catstrain")) {
      items.unshift("<strong>Important:</strong> cats that appear constipated may actually be unable to urinate. A male cat straining with little or no urine should be treated as an emergency.");
    }

    if (context.flags.includes("object")) {
      items.push("If string, thread or tinsel is visible from the mouth or back end, <strong>do not pull it</strong>. Let a veterinarian remove it.");
    }

    this.renderList(this.elements.doNow, items);
    this.renderList(this.elements.watch, [
      "Collapse, weakness or pale gums",
      "Breathing that becomes fast or laboured",
      "Repeated vomiting or a bloated, painful abdomen",
      "Any sudden deterioration — then go to an emergency vet"
    ]);

    this.elements.ctaHeading.textContent = "Not sure it can wait? Ask a vet now.";
    this.elements.ctaText.textContent = "A veterinarian can help decide whether this is a same-day appointment or an immediate emergency visit.";
    this.renderCta(chatUrl, "Chat to a certified vet");
  }

  renderMonitorResult(chatUrl) {
    this.elements.resultHead.className = "s-emergency-vet-triage-tool__result-head is-low";
    this.elements.resultTag.textContent = "Likely OK to monitor";
    this.elements.resultTitle.textContent = "You can probably watch at home for now.";
    this.elements.resultSubtitle.textContent = "Nothing selected clearly indicates an emergency. Monitor closely and seek veterinary advice if anything changes or you remain uneasy.";
    this.elements.doNowHeading.textContent = "What to do";

    this.renderList(this.elements.doNow, [
      "Keep your pet quiet, comfortable and hydrated.",
      "Watch closely over the next 12–24 hours and note any new signs.",
      "Book a routine vet visit if the problem has not improved within a day or two.",
      "Trust your judgement and seek advice sooner if something feels wrong."
    ]);

    this.renderList(this.elements.watch, [
      "Stopping eating or drinking, or becoming flat and weak",
      "Vomiting or diarrhoea that increases",
      "Any trouble breathing or pale gums",
      "Straining to urinate or a swollen abdomen — these mean go now"
    ]);

    this.elements.ctaHeading.textContent = "Want a vet to put your mind at ease?";
    this.elements.ctaText.textContent = "Describe what you are seeing to a veterinarian and get advice without guessing whether a clinic visit is needed.";
    this.renderCta(chatUrl, "Ask a certified vet");
  }

  renderList(element, items) {
    element.innerHTML = items.map((item) => `<li>${item}</li>`).join("");
  }

  renderCta(url, label) {
    const safeUrl = this.escapeAttribute(url || "/");
    const safeLabel = this.escapeHtml(label);

    this.elements.ctaActions.innerHTML = `<a href="${safeUrl}">${safeLabel}</a>`;
  }

  showError(message) {
    this.elements.error.textContent = message;
    this.elements.error.classList.add("is-visible");
    this.elements.error.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }

  clearError() {
    this.elements.error.textContent = "";
    this.elements.error.classList.remove("is-visible");
  }

  restart() {
    this.elements.form?.reset();
    this.elements.followups.innerHTML = "";
    this.elements.result.hidden = true;
    this.clearError();

    this.querySelectorAll(".s-emergency-vet-triage-tool__red-flag-item.is-selected").forEach((item) => {
      item.classList.remove("is-selected");
    });

    this.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  formatPhone(value) {
    const digits = String(value).replace(/\D/g, "");

    if (digits.length === 10) {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
    }

    return value;
  }

  escapeHtml(value) {
    const div = document.createElement("div");
    div.textContent = value;
    return div.innerHTML;
  }

  escapeAttribute(value) {
    return this.escapeHtml(String(value)).replace(/`/g, "&#96;");
  }
}

if (!customElements.get("s-emergency-vet-triage-tool")) {
  customElements.define("s-emergency-vet-triage-tool", SEmergencyVetTriageTool);
}
